import SvgQuoteMarks from '@img/icons/quote-marks.svg?url';
import { css } from 'styled-components';
import { transition } from './animate';
import brand from './brand';
import { fluid } from './fluid';
import fonts from './fonts';
import { Device, from } from './media';
export const headingStyles = css `
  ${fonts.apparat.semiBold};

  color: currentColor;
  margin: 0 0 ${fluid(16, 24)};

  @media ${from(Device.TabletLarge)} {
    ${fonts.apparat.medium};
  }
`;
export const h1 = css `
  ${headingStyles};
  font-size: ${fluid(30, 70)};
  line-height: ${fluid(40, 80)};
`;
export const h2 = css `
  ${headingStyles};
  font-size: ${fluid(25, 60)};
  line-height: ${fluid(35, 70)};
`;
export const h3 = css `
  ${headingStyles};
  font-size: ${fluid(22, 50)};
  line-height: ${fluid(32, 60)};
`;
export const h4 = css `
  ${headingStyles};
  font-size: ${fluid(20, 40)};
  line-height: ${fluid(30, 50)};
`;
export const h5 = css `
  ${headingStyles};
  font-size: ${fluid(18, 30)};
  line-height: ${fluid(24, 38)};
`;
export const h6 = css `
  ${headingStyles};
  font-size: ${fluid(17, 29)};
  line-height: ${fluid(23, 37)};
`;
export const paragraphStyles = css `
  color: currentColor;
  ${fonts.apparat.light};
  margin: 0 0 ${fluid(16, 24)};
`;
export const paragraphLarge = css `
  ${paragraphStyles};
  font-size: ${fluid(20, 26)};
  line-height: ${fluid(30, 36)};
`;
export const paragraphMedium = css `
  ${paragraphStyles};
  font-size: ${fluid(18, 22)};
  line-height: ${fluid(28, 32)};
`;
export const paragraphSmall = css `
  ${paragraphStyles};
  font-size: ${fluid(16, 18)};
  line-height: ${fluid(26, 28)};
`;
export const paragraphMicro = css `
  ${paragraphStyles};
  font-size: ${fluid(12, 14)};
  line-height: ${fluid(22, 24)};
`;
export const meta = css `
  display: block;
  ${fonts.apparat.regular};
  font-size: ${fluid(12, 18)};
  line-height: ${fluid(16, 24)};
  letter-spacing: 0.01em;
  text-transform: uppercase;

  sup {
    font-size: 60%;
  }
`;
export const link = css `
  display: inline-block;
  color: currentColor;
  background: linear-gradient(
    to right,
    var(--accentColor, ${brand.primary.orange}),
    var(--accentColor, ${brand.primary.orange})
  );
  background-repeat: no-repeat;
  background-size: 0% 100%;
  margin: 0 -4px 0;
  padding: 0 4px 0;
  text-decoration: underline;
  text-underline-position: under;
  ${transition('background-size, color')};

  &:focus {
    outline: none;
  }

  &:focus,
  &:hover {
    background-size: 100% 100%;
    color: ${brand.base.blue};
  }
`;
export const table = css `
  background: ${brand.white};
  border-collapse: collapse;
  color: ${brand.base.blue};
  margin-bottom: 1em;
  width: 100%;

  thead {
    tr {
      background: ${brand.primary.blue};

      td,
      th {
        border: 2px solid ${brand.white};
        color: ${brand.white};
        ${fonts.apparat.regular};
        font-size: 18px;
        letter-spacing: -0.01em;
        line-height: 22px;
        padding: 12px 16px;
        text-align: left;
      }
    }
  }

  tbody {
    tr {
      &:nth-child(odd) {
        background: ${brand.lightBlue};
      }

      &:nth-child(even) {
        background: ${brand.white};
      }

      th {
        border: 2px solid ${brand.white};
        color: ${brand.white};
        ${fonts.apparat.medium};
        font-size: 18px;
        letter-spacing: -0.01em;
        line-height: 22px;
        padding: 12px 16px;
        text-align: left;
        text-transform: uppercase;
      }

      td {
        border: 2px solid ${brand.white};
        ${fonts.apparat.regular};
        font-size: 16px;
        letter-spacing: -0.01em;
        line-height: 20px;
        padding: 12px 16px;
        text-align: left;
      }
    }
  }
`;
export const blockquote = css `
  ${paragraphMedium}
  ${fonts.apparat.regularItalic}

  position: relative;
  text-align: left;
  margin-bottom: 38px;

  @media ${from(Device.TabletLarge)} {
    ${paragraphLarge}
    ${fonts.apparat.regularItalic}
    padding: 28px 0 0 76px;
  }

  &::before {
    content: '';
    display: block;
    width: ${fluid(40, 56)};
    height: ${fluid(38, 52)};
    margin-bottom: 20px;
    background: url(${SvgQuoteMarks}) center no-repeat;

    @media ${from(Device.TabletLarge)} {
      position: absolute;
      top: 0;
      left: 0;
      margin-bottom: unset;
    }
  }

  + P {
    ${paragraphMedium}
    margin-bottom: ${fluid(5, 10)};
  }

  + p,
  + p + p {
    @media ${from(Device.TabletLarge)} {
      padding: 0 0 0 76px;
    }
  }
`;
export const contentStyles = css `
  h1,
  .h1-styles {
    ${h2}
  }

  h2,
  .h2-styles {
    ${h4}
  }

  h3,
  .h3-styles {
    ${headingStyles};

    font-size: ${fluid(19, 35)};
    line-height: ${fluid(28, 45)};
  }

  h4,
  .h4-styles {
    ${h5}
  }

  h5,
  .h5-styles {
    ${h6}
  }

  p.large {
    ${paragraphLarge}
  }

  p {
    ${paragraphMedium}
  }

  p.small {
    ${paragraphSmall}
  }

  p.meta {
    ${meta}
  }

  a {
    ${link}
  }

  ol,
  ul {
    ${paragraphMedium};

    li {
      &::marker {
        color: ${brand.primary.orange};
      }
    }
  }

  ol {
    list-style: decimal;
    padding-left: 1.2em;
  }

  ul {
    list-style: disc;
    padding-left: 1.2em;

    li {
      margin-bottom: 16px;

      &:last-child {
        margin-bottom: 0;
      }
    }

    p {
      &:last-child {
        margin-bottom: 0;
      }
    }
  }

  hr {
    display: block;
    background: currentColor;
    border: 0;
    height: 4px;
    margin: ${fluid(40, 80)} 0;
    padding: 0;
    width: 100%;
  }

  table {
    ${table};
  }

  blockquote {
    ${blockquote};
  }

  .responsive-table {
    position: relative;
    overflow-x: auto;
  }

  > *:first-child {
    margin-top: 0;
  }

  > *:last-child {
    margin-bottom: 0;
  }
`;
